import React, { useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import MenuPhotoSwipe from '@discover-components/restaurant/menu/photoSwipe';
import useLightbox from '@discover-components/restaurant/menu/useLightbox';
import { findMaxLastEdited, getMenuPages } from '@discover-components/restaurant/menu/utils';
import PropTypes from 'prop-types';
import { Title, Box, Link, Divider, List } from '@firsttable/web-components/atoms';
import { styles as linkStyles } from '@firsttable/web-components/atoms/Link';
import { spaces } from '@firsttable/web-theme';
import { awsImage } from '@firsttable/functions';
import Flex from '@firsttable/web-components/atoms/Flex/Flex';
import IconSpoonM from '@firsttable/web-components/atoms/Icon/IconSpoonM';
import IconMapM from '@firsttable/web-components/atoms/Icon/IconMapM';
import IconBookM from '@firsttable/web-components/atoms/Icon/IconBookM';
import IconContacts from '@firsttable/web-components/atoms/Icon/IconContacts';
import IconPayment from '@firsttable/web-components/atoms/Icon/IconPayment';
import { getRestaurantMenuLink, getRestaurantMenuPaths } from '@/lib/restaurantMenu';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import withAnalyticsComponent from '@/components/analytics/hocs/withAnalyticsComponent';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import globalState from '@/lib/globalState';
import { useOpenModal } from '../../context/ModalContext';
import Amex from '../../molecules/Amex';
import MapModal from '../Modal/Restaurant/Map';
import CloudfrontImage from '../../CloudfrontImage';
import Contacts from './Contacts';
import Content from './Content';

// A real anchor so the crawlable /menu page is one modified click or new tab away.
const MenuLink = styled.a.withConfig({
  shouldForwardProp: (prop: string) => prop !== 'underline' && prop !== 'expandClickableArea',
})<{ underline: string; expandClickableArea: boolean }>`
  ${linkStyles};
`;

const Tags = ({ tags }) => {
  const byCategory = tags.edges.reduce((acc, tag) => {
    acc[tag.node.category] = acc[tag.node.category] || [];
    acc[tag.node.category].push(tag.node.title);
    return acc;
  }, {});

  const currencySymbol = globalState?.siteConfig?.currencySymbol || '$';

  return (
    <Box fontSize="xxs">
      <List pure>
        {Object.keys(byCategory)
          .sort()
          .map((category) => (
            <li key={category}>
              <b>{category}:</b>{' '}
              {category === 'Price' && currencySymbol !== '$'
                ? byCategory[category].join(', ').replaceAll('$', currencySymbol)
                : byCategory[category].join(', ')}
            </li>
          ))}
      </List>
    </Box>
  );
};

const StyledDivider = () => (
  <Divider
    top="-5px"
    left="-2px"
    style={{ transform: 'rotate(-67.69deg)' }}
    bg="black"
    height="1px"
    width="29px"
    display={['none', 'inline-block']}
    ml="7px"
  />
);

const restaurantInfoDefaultRestaurant: any = {};
const RestaurantInfo = ({ restaurant = restaurantInfoDefaultRestaurant, setState, ...rest }) => {
  const props = { restaurant, setState, ...rest };
  const router = useRouter();
  const { track } = useAnalytics();
  const lightbox = useLightbox();
  const showModal = useOpenModal();
  const [toggleContactsState, setToggleContactsState] = useState(false);

  const { cuisines } = restaurant;
  const toggleContacts = () => setToggleContactsState(!toggleContactsState);

  const menuPages = getMenuPages(restaurant.menus || { edges: [] }).filter((page) => page.url);
  const externalMenuUrl = getRestaurantMenuLink(restaurant);
  // Static renders have no query string, so wait for the router to keep hydration consistent.
  const { menuPath } = getRestaurantMenuPaths(restaurant.slug, router.isReady ? router.asPath : '');
  const LinkWithTracking = withAnalyticsComponent(Link, 'Link');

  return (
    <Box {...spaces(props)} mb={['20px', '30px']} pb="1px">
      <TrackProvider section="overview">
        <Title mb="s">
          {restaurant.id === 6025 && restaurant.logo && (
            <Box mb="20px" width={[100, 140]} height={[100, 140]} style={{ float: 'right' }}>
              <CloudfrontImage
                src={awsImage(restaurant.logo.url, 200, 200)}
                alt={restaurant.name}
                width={140}
                height={140}
              />
            </Box>
          )}
          Overview
        </Title>
        <Box mb="15px" display={['block', 'flex']} flexWrap="wrap">
          {cuisines.edges.length > 0 && (
            <Box mb="xs">
              <IconSpoonM mr="10px" top="-1px" color="gold.900" />
              {cuisines.edges
                .filter(({ node }) => node.category === 'Cuisine')
                .map(({ node }) => node.title)
                .join(', ')}
              <StyledDivider />
            </Box>
          )}
          <Box mb="xs">
            <IconMapM mr="10px" top="-1px" color="gold.900" />
            <TrackProvider link_name="map" link_type="navigation">
              <LinkWithTracking
                onClick={() =>
                  showModal(<MapModal restaurant={restaurant} />, {
                    closeIconColor: 'brand.blue',
                  })
                }
                underline>
                Location
              </LinkWithTracking>
            </TrackProvider>
            <StyledDivider />
          </Box>
          {(menuPages.length > 0 || externalMenuUrl) && (
            <Box mb="xs" minWidth={0} maxWidth="100%">
              <IconBookM mr="10px" top="-1px" color="gold.900" />
              <TrackProvider
                link_name={menuPages.length > 0 ? 'sample menu' : 'menu'}
                {...(menuPages.length > 0 ? { link_type: 'toggle' } : {})}>
                <MenuLink
                  href={menuPath}
                  aria-haspopup={menuPages.length > 0 ? 'dialog' : undefined}
                  underline="true"
                  expandClickableArea={false}
                  className="whitespace-normal break-words"
                  onClick={(event) => {
                    // Preserve the reporting label independently of the visible restaurant name.
                    track('link_clicked', { link_copy: 'Menu', href: menuPath });
                    // A modified click follows the href to the menu page. A plain click opens the menu lightbox the city-page cards use and leaves the address on the restaurant; an external-only menu has no pages, so it follows the href.
                    if (
                      menuPages.length === 0 ||
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    )
                      return;
                    event.preventDefault();
                    lightbox.open();
                    setState({ sampleMenu: { isOpen: true } });
                  }}>
                  {restaurant.menuTitle} Menu
                </MenuLink>
              </TrackProvider>
              <StyledDivider />
            </Box>
          )}
          <Box mb="xs">
            <IconContacts mr="10px" top="-1px" color="gold.900" />
            <TrackProvider link_name="contacts" link_type="toggle">
              <LinkWithTracking underline={!toggleContactsState} onClick={toggleContacts}>
                Contact
              </LinkWithTracking>
            </TrackProvider>
            {restaurant.acceptsAmex && <StyledDivider />}
          </Box>
          {restaurant.acceptsAmex && (
            <Flex mb="xs">
              <IconPayment mr="10px" top="3px" color="gold.900" />
              <Box>
                <Amex />
              </Box>
            </Flex>
          )}
        </Box>
        <MenuPhotoSwipe
          items={menuPages}
          isOpen={lightbox.isOpen}
          index={lightbox.index}
          showBackground={lightbox.showBackground}
          onChange={lightbox.onChange}
          onClose={() => {
            lightbox.close();
            setState({ sampleMenu: { isOpen: false } });
          }}
          lastUpdated={findMaxLastEdited(restaurant.menus)}
        />
        <Contacts toggleContactsState={toggleContactsState} restaurant={restaurant} />
        <Content content={restaurant.content} defaultExpandedWhenOverflowing={false}>
          {restaurant?.cuisines?.edges?.length > 0 && <Tags tags={restaurant.cuisines} />}
        </Content>
      </TrackProvider>
    </Box>
  );
};

RestaurantInfo.propTypes = {
  restaurant: PropTypes.object,
  setState: PropTypes.func,
};

export default RestaurantInfo;
